import { useI18n } from 'vue-i18n';
import { isNil } from 'lodash';

import userService from '@satellite/services/user-service';
import { getLocalStorageValue, setLocalStorageValue } from './useLocalStorage';
import {
  i18nLocaleToPreferredLanguage,
  isAppSupportedI18nLocale,
  preferredLanguageToI18nLocale,
  LOCALE_STORAGE_KEY
} from './useLocaleHelpers';

export type UserPreferredLanguage = {
  id?: string;
  preferredLanguage?: string | null;
};

/**
 * Syncs vue-i18n locale and user-scoped localStorage with the user's persisted `preferredLanguage`.
 * DB is the source of truth on login for app UI locales ({@link localeToReadableLanguageMap}).
 * When the DB tag is missing or not an app UI locale, user-scoped localStorage is applied and
 * PATCHed back to the API when it holds a supported locale. If user-scoped localStorage is empty,
 * the guest `language` key is migrated on login whenever the DB has no app-supported preference.
 * If storage is still empty, the active vue-i18n locale is persisted for the user on first login.
 *
 * Writes use {@link i18nLocaleToPreferredLanguage} (BCP 47 normalization, same as Neutron).
 * Reads use {@link preferredLanguageToI18nLocale} (app UI locales only).
 */
export function usePreferredLanguage() {
  const { locale } = useI18n({ useScope: 'global' });

  function getAppLocale(userIdValue?: string): string | undefined {
    const storedLocale = getLocalStorageValue<string>(
      LOCALE_STORAGE_KEY,
      undefined,
      userIdValue ? { userId: userIdValue } : undefined
    );
    if (storedLocale && isAppSupportedI18nLocale(storedLocale)) {
      return storedLocale.toLowerCase();
    }

    return undefined;
  }

  function getLoginFallbackLocale(
    user: UserPreferredLanguage & { id: string }
  ): string | undefined {
    const storedUserLocale = getAppLocale(user.id);
    if (storedUserLocale) {
      return storedUserLocale;
    }

    if (!preferredLanguageToI18nLocale(user.preferredLanguage)) {
      return getAppLocale();
    }

    return undefined;
  }

  function applyAndPersistI18nLocale(userIdValue: string | undefined, i18nLocale: string) {
    locale.value = i18nLocale;

    if (userIdValue) {
      setLocalStorageValue(LOCALE_STORAGE_KEY, i18nLocale, { userId: userIdValue });
      return;
    }

    setLocalStorageValue(LOCALE_STORAGE_KEY, i18nLocale);
  }

  function applyPreferredLanguageFromUser(user: UserPreferredLanguage) {
    const i18nLocale = preferredLanguageToI18nLocale(user.preferredLanguage);
    if (!i18nLocale) {
      return;
    }

    applyAndPersistI18nLocale(user.id, i18nLocale);
  }

  async function persistPreferredLanguageToApi(userId: string, preferredLanguage: string) {
    try {
      await userService.updateUser(
        userId,
        { preferredLanguage },
        {},
        { suppressNotification: true }
      );
    } catch (error) {
      console.warn('[usePreferredLanguage] Failed to persist preferredLanguage:', error);
    }
  }

  async function persistPreferredLanguageIfNeeded(user: UserPreferredLanguage & { id: string }) {
    if (preferredLanguageToI18nLocale(user.preferredLanguage)) {
      return;
    }

    const storedUserLocale = getAppLocale(user.id);
    const preferredLanguage = storedUserLocale
      ? i18nLocaleToPreferredLanguage(storedUserLocale)
      : undefined;
    if (!preferredLanguage) {
      return;
    }

    await persistPreferredLanguageToApi(user.id, preferredLanguage);
  }

  async function persistPreferredLanguageOnChange(userId: string, newLocale: string) {
    const preferredLanguage = i18nLocaleToPreferredLanguage(newLocale);
    if (!preferredLanguage) {
      return;
    }

    await persistPreferredLanguageToApi(userId, preferredLanguage);
  }

  async function syncPreferredLanguageOnLogin(user: UserPreferredLanguage & { id: string }) {
    const dbLocale = preferredLanguageToI18nLocale(user.preferredLanguage);
    if (dbLocale) {
      applyPreferredLanguageFromUser(user);
      return;
    }

    const localeToApply = getLoginFallbackLocale(user);
    if (localeToApply) {
      applyAndPersistI18nLocale(user.id, localeToApply);
      await persistPreferredLanguageIfNeeded(user);
      return;
    }

    const hasDbPreference = !isNil(user.preferredLanguage) && user.preferredLanguage.trim() !== '';
    const hasUserStorageKey =
      typeof window !== 'undefined' &&
      !isNil(window.localStorage.getItem(`${LOCALE_STORAGE_KEY}_${user.id}`));

    if (!hasDbPreference && !hasUserStorageKey && isAppSupportedI18nLocale(locale.value)) {
      applyAndPersistI18nLocale(user.id, locale.value.toLowerCase());
      await persistPreferredLanguageIfNeeded(user);
    }
  }

  return {
    applyPreferredLanguageFromUser,
    persistPreferredLanguageIfNeeded,
    persistPreferredLanguageOnChange,
    syncPreferredLanguageOnLogin
  };
}
